import { faExternalLinkAlt, faFrown, faSmile } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { memo } from 'react';
import { Link } from 'react-router-dom';

import { FeedbackSentiment } from '@/types/appointmentAttendanceFeedback';
import { daysBetween } from '@/utils/dates';

import { Props } from './types';

const FeedbackCard = ({
  feedback,
  personId,
  title = 'Feedback',
  showTitle = true,
  showLink = true,
  showTimestamp = true,
  truncateText = true,
  minimal = false
}: Props) => {
  const { sentiment, comment, appointment_reason_name, other_reason, appointment_id, created_at } =
    feedback;
  const isPositive = sentiment === FeedbackSentiment.POSITIVE;
  const days = daysBetween(new Date(created_at), new Date(), true);
  const timeAgoText = days === '1' ? '1 day ago' : `${days} days ago`;

  return (
    <div className="mb-4">
      {showTitle && <h3 className="text-lg font-medium mb-2">{title}</h3>}
      <div
        className={`flex items-stretch gap-3 ${minimal ? '' : 'p-4 border border-gray-300 rounded-lg bg-white shadow-sm'}`}>
        <div className="flex-shrink-0 self-stretch">
          {minimal ? (
            <div className={`w-2 h-full rounded ${isPositive ? 'bg-green-300' : 'bg-red-300'}`} />
          ) : (
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isPositive ? 'bg-green-300' : 'bg-red-300'
              }`}>
              <FontAwesomeIcon
                icon={isPositive ? faSmile : faFrown}
                className={`text-xl ${isPositive ? 'text-green-600' : 'text-red-600'}`}
              />
            </div>
          )}
        </div>
        <div className="flex-1 space-y-1">
          {appointment_reason_name ? (
            <h2
              className={`font-semibold text-gray-900 dark:text-gray-100 ${minimal ? 'text-sm' : 'text-xl'}`}>
              {appointment_reason_name}
            </h2>
          ) : isPositive ? (
            <h2
              className={`font-semibold text-gray-900 dark:text-gray-100 ${minimal ? 'text-sm' : 'text-xl'}`}>
              Good
            </h2>
          ) : null}
          {other_reason && (
            <p
              className={`text-gray-700 dark:text-gray-300 italic ${truncateText ? 'line-clamp-1' : ''}`}>
              &quot;{other_reason}&quot;
            </p>
          )}
          {comment && (
            <div className="mt-2">
              {!minimal && (
                <p className="text-xs text-gray-500 dark:text-gray-400 font-medium mb-0.5">
                  Additional feedback:
                </p>
              )}
              <p
                className={`text-gray-600 dark:text-gray-300 italic text-sm ${truncateText ? 'line-clamp-1' : ''}`}>
                &quot;{comment}&quot;
              </p>
            </div>
          )}
        </div>
        {!minimal && (showLink || showTimestamp) && (
          <div className="flex-shrink-0 flex flex-col items-end gap-1">
            {showLink && personId && (
              <Link
                to={`/people/${personId}/appointments/${appointment_id}`}
                className="text-blue-600 hover:text-blue-800"
                aria-label="Open feedback details">
                <FontAwesomeIcon icon={faExternalLinkAlt} />
              </Link>
            )}
            {showTimestamp && (
              <span className="text-xs text-gray-500 dark:text-gray-400">{timeAgoText}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(FeedbackCard);
